/**
 * Email-content helpers for Assessment Performance — backport variant.
 *
 * Mirrors `src/entities/assessment-performance/lib/buildEmailContent.js`
 * on refactor/mui-v9-migration so the merged FSD branch can drop this
 * file without behavior change. Two differences from the FSD copy:
 *
 *   1. Period range is `{ start, end }` ISO-day strings (slice shape
 *      on this branch), not `{ startDate, endDate }` Date objects.
 *   2. Metric dictionary + delta math live inline here so this module
 *      stays self-contained — there is no `entities/lib/computeDelta`
 *      on the legacy branch.
 *
 * No React. Pure functions only.
 *
 * TEMP: orphan after FSD merge.
 */
import { format } from 'date-fns';
import copy from 'copy-to-clipboard';

// ----- metric meta -------------------------------------------

const CATEGORY = { performance: 'performance', monitor: 'monitor', neutral: 'neutral' };
const TONE = { good: 'good', bad: 'bad', attention: 'attention', neutral: 'neutral' };

const METRIC_META = {
  sent:              { category: CATEGORY.neutral,                       },
  completed:         { category: CATEGORY.performance                    },
  inProgress:        { category: CATEGORY.monitor                        },
  noShow:            { category: CATEGORY.neutral                        },
  invited:           { category: CATEGORY.performance, goodWhenDown: true },
  eTransfer:         { category: CATEGORY.performance                    },
  combinedCompleted: { category: CATEGORY.performance                    },
  partialProcessed:  { category: CATEGORY.monitor                        },
  pendingReview:     { category: CATEGORY.neutral                        },
};

// Order matches Aly's monthly review email exactly. Partial Processed
// and Pending Review intentionally do NOT appear in the per-account
// block — they are explained in the Quick definitions block.
const EMAIL_METRIC_ORDER = [
  { id: 'sent',              label: 'Sent' },
  { id: 'completed',         label: 'Completed' },
  { id: 'inProgress',        label: 'In Progress' },
  { id: 'noShow',            label: 'No Show' },
  { id: 'invited',           label: 'Invited' },
  { id: 'eTransfer',         label: 'E-Transfer' },
  { id: 'combinedCompleted', label: 'Combined Completed + E-Transfer' },
];

// ----- delta math --------------------------------------------

const isNumber = (v) => typeof v === 'number' && Number.isFinite(v);

const computePercentDelta = (current, prior) => {
  if (!isNumber(current) || !isNumber(prior)) return null;
  if (prior === 0 && current === 0) return 0;
  if (prior === 0) return current > 0 ? 100 : -100;
  return Math.round(((current - prior) / Math.abs(prior)) * 1000) / 10;
};

const resolveDeltaTone = (metricId, percentDelta) => {
  const meta = METRIC_META[metricId];
  if (!meta) return TONE.neutral;
  if (percentDelta === null || percentDelta === undefined) return TONE.neutral;
  if (percentDelta === 0) return TONE.neutral;
  if (meta.category === CATEGORY.neutral) return TONE.neutral;
  const isUp = percentDelta > 0;
  if (meta.category === CATEGORY.monitor) {
    return isUp ? TONE.attention : TONE.good;
  }
  // performance
  const goodDirection = meta.goodWhenDown ? !isUp : isUp;
  return goodDirection ? TONE.good : TONE.bad;
};

const formatDeltaPercent = (percentDelta, { noChangeLabel = 'no change' } = {}) => {
  if (percentDelta === null || percentDelta === undefined) return '—';
  if (percentDelta === 0) return noChangeLabel;
  const abs = Math.abs(percentDelta);
  // Unicode minus (U+2212) for tabular alignment, ASCII plus for positive.
  const sign = percentDelta > 0 ? '+' : '−';
  return `${sign}${abs}%`;
};

// Mail clients don't render in-app colours through mailto:, so we use
// the same UPPERCASE words Aly uses today.
const toneToWord = (tone, percentDelta) => {
  if (percentDelta === 0 || percentDelta == null) return 'no change';
  if (tone === TONE.good) return 'GREEN';
  if (tone === TONE.bad || tone === TONE.attention) return 'RED';
  return ''; // neutral metric — no verdict word
};

// ----- date format -------------------------------------------

const formatPeriod = (range) => {
  if (!range?.start || !range?.end) return '—';
  const start = new Date(range.start);
  const end = new Date(range.end);
  return `${format(start, 'MMM d, yyyy')} – ${format(end, 'MMM d, yyyy')}`;
};

// ----- per-account block -------------------------------------

const formatMetricLine = (label, value, previousValue, metricId) => {
  const pct = computePercentDelta(value, previousValue);
  if (pct === 0 || pct == null) {
    return `${label}: ${previousValue} → ${value} (no change)`;
  }
  const tone = resolveDeltaTone(metricId, pct);
  const word = toneToWord(tone, pct);
  const deltaLabel = formatDeltaPercent(pct, { noChangeLabel: 'no change' });
  const suffix = word ? ` ${word}` : '';
  return `${label}: ${previousValue} → ${value} (${deltaLabel}${suffix})`;
};

const buildAccountBlockLines = (metricsObj, displayName) => {
  const lines = [displayName];
  EMAIL_METRIC_ORDER.forEach(({ id, label }) => {
    const cell = metricsObj?.[id];
    if (!cell) return;
    const value = cell.value ?? 0;
    const previousValue = cell.previousValue ?? 0;
    lines.push(formatMetricLine(label, value, previousValue, id));
  });
  return lines;
};

// ============== Public builders ==============

export const buildAccountEmailSubject = (accountName, periodA, periodB) =>
  `QHS — ${accountName} performance review: ${formatPeriod(periodB)} to ${formatPeriod(periodA)}`;

export const buildFullReviewEmailSubject = (periodA, periodB) =>
  `QHS — Performance review: ${formatPeriod(periodB)} to ${formatPeriod(periodA)}`;

/**
 * Single-account email body — drilldown modal action.
 */
export const buildAccountEmailBody = (accountName, metricsObj, periodA, periodB) => {
  const accountBlock = buildAccountBlockLines(metricsObj, accountName);
  return [
    'Hi [RECIPIENT FIRST NAME],',
    '',
    '[INSERT INTRODUCTION / CONTEXT PARAGRAPH HERE — e.g. who you are, why you are sending this email, what the recipient should know about the period under review.]',
    '',
    `Below is the breakdown for ${accountName}:`,
    '',
    ...accountBlock,
    '',
    `Period under review: ${formatPeriod(periodA)} (current) vs ${formatPeriod(periodB)} (previous).`,
    '',
    '[INSERT SUMMARY OBSERVATION — e.g. overall volume trend, which metric stood out, where the main opportunity is.]',
    '',
    'Quick definitions:',
    '',
    'Partial Processed = patient is halfway or more completed but did not finish. This status is now live and is our top priority; we will discuss it on the live call.',
    'In Progress = patient opened and only entered DOB, did not continue.',
    'Invited = patient received but did not open.',
    '',
    'Please also continue marking No Show / Cancel correctly so tracking remains accurate.',
    '',
    'Goal moving forward: [INSERT GOAL — e.g. Increase the total completion rate by at least 5% month over month.]',
    '',
    '[INSERT CLOSING PARAGRAPH — e.g. ask about upcoming account closures, holidays, staffing changes, or schedule a sync to align on workflow.]',
    '',
    'Looking forward to working together.',
    '',
    '[INSERT YOUR SIGNATURE]',
  ].join('\n');
};

/**
 * Full-review email body — every account in `rows` plus an optional
 * totals block. Mirrors Aly's monthly review email format.
 */
export const buildFullReviewEmailBody = ({ rows, totals, periodA, periodB }) => {
  const accountBlocks = (rows || []).flatMap((row) => [
    ...buildAccountBlockLines(row.metrics, row.name || row.uuid || '—'),
    '',
  ]);
  const totalsBlock = totals
    ? [
        'TOTAL ACROSS ALL ACCOUNTS',
        ...buildAccountBlockLines(totals.metrics, 'All accounts'),
        '',
      ]
    : [];

  return [
    'Hi [RECIPIENT FIRST NAME],',
    '',
    '[INSERT INTRODUCTION / CONTEXT PARAGRAPH HERE — e.g. who you are and what this email is about. Aly typically introduces herself, mentions she works alongside the team on ISP operations, workflow, and performance reporting, and frames this email as the monthly review.]',
    '',
    `I also wanted to share a comparison of ${formatPeriod(periodB)} to ${formatPeriod(periodA)} across all accounts and begin aligning on next steps. Below is the breakdown by account:`,
    '',
    ...accountBlocks,
    ...totalsBlock,
    '[INSERT SUMMARY PARAGRAPH — e.g. overall volume trend, total completions including E-Transfers, where the main opportunity remains. Example phrasing: "Overall volume is increasing across most accounts, and total completions (including E-Transfers) are improving in several accounts, which is a positive. The main opportunity remains with patients who do not complete the assessment after receiving it."]',
    '',
    'Quick definitions:',
    '',
    'Partial Processed = patient is halfway or more completed but did not finish. This status is now live and is our top priority; we will discuss it on the live call.',
    'In Progress = patient opened and only entered DOB, did not continue.',
    'Invited = patient received but did not open.',
    '',
    'Please also continue marking No Show / Cancel correctly so tracking remains accurate.',
    '',
    'Goal moving forward: [INSERT GOAL — e.g. Increase the total completion rate by at least 5% month over month. Aly will send the next review in the first week of the following month.]',
    '',
    '[INSERT CLOSING PARAGRAPH — e.g. ask about upcoming account closures, holidays, vacations, provider time off, staffing changes, or workflow updates. Optionally propose a sync to align on workflow and verbalization across all accounts.]',
    '',
    'Looking forward to working together.',
    '',
    '[INSERT YOUR SIGNATURE]',
  ].join('\n');
};

// ----- mailto / clipboard / opener ---------------------------

// Empirically: Gmail returns HTTP 400 around ~10 KB; Outlook web is
// similarly fussy; Apple Mail tolerates up to ~8 KB. Single-account
// bodies sit ~1.7 KB raw / ~2.2 KB encoded; the multi-account monthly
// review (17 accounts) hits ~10 KB. Cutoff at 4000 chars keeps the
// single-account flow inline and routes the multi-account flow to
// the manual-copy dialog.
export const MAILTO_INLINE_LIMIT = 4000;

export const buildMailtoHref = ({ subject, body }) =>
  `mailto:?subject=${encodeURIComponent(subject || '')}${
    body ? `&body=${encodeURIComponent(body)}` : ''
  }`;

export const canInlineDraft = ({ subject, body }) =>
  buildMailtoHref({ subject, body }).length <= MAILTO_INLINE_LIMIT;

const triggerAnchor = (href) => {
  // Use a short-lived anchor instead of `window.location.href = ...`
  // so the page itself doesn't navigate if the OS mail handler is
  // unset (some Linux configs would otherwise blank the page).
  const link = document.createElement('a');
  link.href = href;
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const openDraftEmail = ({ subject, body }) => {
  triggerAnchor(buildMailtoHref({ subject, body }));
};

export const openSubjectOnlyDraft = ({ subject }) => {
  triggerAnchor(buildMailtoHref({ subject, body: '' }));
};

/**
 * Copy the supplied text to the clipboard. Returns true on success.
 * Wraps the copy-to-clipboard npm package so HTTP dev hosts (where
 * navigator.clipboard.writeText silently rejects) still work via the
 * execCommand fallback.
 */
export const copyTextToClipboard = (text) => {
  try {
    return copy(text);
  } catch (e) {
    return false;
  }
};
